import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  IconButton,
  Chip,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  DragIndicator as DragIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { ActiveFilter, FilterConfig } from '../../../../types/filters';
import { FilterDialog } from './FilterDialog';
import { ColumnSelector, TableColumn } from '../ColumnSelector';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getSelectedTabBackground,
  getSelectedTabBorder,
  getSelectedTabHoverBackground,
} from '../../utils/navigationColors';
import { useCloseOverlayWhenPaneInactive } from '../../hooks/useTabScopedOpen';

// Componente para item de coluna arrastável
const DraggableColumnItem: React.FC<{
  column: TableColumn;
  onVisibilityChange: (columnId: string) => void;
  enableDragAndDrop?: boolean;
}> = ({ column, onVisibilityChange, enableDragAndDrop = true }) => {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: column.id,
    disabled: !enableDragAndDrop
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...(enableDragAndDrop ? attributes : {})}
      {...(enableDragAndDrop ? listeners : {})}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 0.5,
        borderRadius: 1,
        backgroundColor: isDragging 
          ? theme.palette.mode === 'dark' 
            ? 'rgba(25, 118, 210, 0.2)' 
            : 'rgba(25, 118, 210, 0.1)'
          : theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)' 
            : 'transparent',
        border: isDragging 
          ? `2px dashed ${theme.palette.primary.main}` 
          : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'transparent'}`,
        boxShadow: isDragging 
          ? theme.palette.mode === 'dark'
            ? '0 8px 25px rgba(0,0,0,0.3)'
            : '0 8px 25px rgba(0,0,0,0.15)'
          : theme.palette.mode === 'dark'
            ? '0 2px 4px rgba(0,0,0,0.2)'
            : '0 2px 4px rgba(0,0,0,0.1)',
        opacity: isDragging ? 0.5 : 1,
        cursor: enableDragAndDrop ? 'grab' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 12px rgba(0,0,0,0.3)'
            : '0 4px 12px rgba(0,0,0,0.15)',
        },
        '&:active': {
          cursor: enableDragAndDrop ? 'grabbing' : 'default',
        },
      }}
    >
      {enableDragAndDrop && (
        <DragIcon 
          sx={{ 
            fontSize: 18, 
            color: isDragging ? theme.palette.primary.main : theme.palette.action.active,
            cursor: 'grab',
          }} 
        />
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={column.visible}
            onChange={() => onVisibilityChange(column.id)}
            disabled={column.required}
            size="small"
            sx={{ p: 0.5 }}
          />
        }
        label={
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" color="text.primary">
              {column.label}
            </Typography>
            {column.required && (
              <Typography variant="caption" color="text.secondary">
                *
              </Typography>
            )}
          </Box>
        }
        sx={{ 
          minWidth: 'fit-content',
          '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
          m: 0
        }}
      />
    </Box>
  );
};

interface DynamicFilterBarProps extends FilterConfig {
  activeFilters: ActiveFilter[];
  onAddFilter: () => void;
  onEditFilter: (filter: ActiveFilter) => void;
  onDeleteFilter: (filterId: string) => void;
  onOpenFilterDialog: () => void;
  filterDialogOpen: boolean;
  onCloseFilterDialog: () => void;
  selectedColumn: string;
  filterValue: string;
  onColumnChange: (value: string) => void;
  onValueChange: (value: string) => void;
  editingFilterId?: string | null; // ID do filtro sendo editado
  // Props para controle de colunas
  tableColumns?: TableColumn[];
  onColumnVisibilityChange?: (columnId: string) => void;
  onColumnReorder?: (columns: TableColumn[]) => void;
  onResetToDefaultColumns?: () => void;
  onShowAllColumns?: () => void;
  enableColumnDragAndDrop?: boolean;
  rightExtraContent?: React.ReactNode;
  leftExtraContent?: React.ReactNode;
  /**
   * `leftExtraContent` com largura elástica (ex.: campo de busca): encolhe no
   * desktop estreito e ocupa a largura livre no mobile, em vez de largura fixa
   * que transborda a toolbar. Default: false (comportamento original).
   */
  leftExtraContentFlexible?: boolean;
  // Props para exportação Excel
  onExportExcel?: () => void;
  showExcelButton?: boolean;
  compactActionButtons?: boolean;
  /**
   * Opt-in: alvos de toque de 44px (botões, refresh, chips). Default `false`: nenhum consumidor muda.
   */
  touchFriendly?: boolean;
  /**
   * Opt-in: diálogo "Adicionar Filtro" em tela cheia. Boolean explícito decidido pelo chamador (ex.:
   * `usePhoneDialog()` do módulo), sem heurística de viewport duplicada aqui.
   */
  fullScreenFilterDialog?: boolean;
}

export const DynamicFilterBar: React.FC<DynamicFilterBarProps> = ({
  columns,
  onCreateNew,
  createButtonText = 'Novo Item',
  showRefreshButton = true,
  onRefresh,
  activeFilters,
  onAddFilter,
  onEditFilter,
  onDeleteFilter,
  onOpenFilterDialog,
  filterDialogOpen,
  onCloseFilterDialog,
  selectedColumn,
  filterValue,
  onColumnChange,
  onValueChange,
  editingFilterId,
  // Props para controle de colunas
  tableColumns,
  onColumnVisibilityChange,
  onColumnReorder,
  onResetToDefaultColumns,
  onShowAllColumns,
  enableColumnDragAndDrop = true,
  rightExtraContent,
  leftExtraContent,
  leftExtraContentFlexible = false,
  onExportExcel,
  showExcelButton = false,
  compactActionButtons = false,
  touchFriendly = false,
  fullScreenFilterDialog = false,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const toolbarRowRef = useRef<HTMLDivElement>(null);
  const toolbarLeftRef = useRef<HTMLDivElement>(null);
  const toolbarRightRef = useRef<HTMLDivElement>(null);
  const [overflowCompact, setOverflowCompact] = useState(false);
  const isCompactActions = isSmallScreen || (compactActionButtons && overflowCompact);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<TableColumn | null>(null);
  const isDark = theme.palette.mode === 'dark';

  useCloseOverlayWhenPaneInactive(filterDialogOpen, onCloseFilterDialog);
  const createButtonBg = getSelectedTabBackground(theme);
  const createButtonBorder = getSelectedTabBorder(theme);
  const createButtonHoverBg = getSelectedTabHoverBackground(theme);

  // Configuração dos sensores para drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Função para lidar com o fim do drag & drop
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && onColumnReorder && tableColumns) {
      const oldIndex = tableColumns.findIndex((col) => col.id === active.id);
      const newIndex = tableColumns.findIndex((col) => col.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newColumns = arrayMove(tableColumns, oldIndex, newIndex);
        onColumnReorder(newColumns);
      }
    }
    
    setDraggedColumn(null);
  };

  // Função para lidar com o início do drag & drop
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const column = tableColumns?.find(col => col.id === active.id);
    if (column) {
      setDraggedColumn(column);
    }
  };

  const toggleColumnSelector = () => {
    setColumnSelectorOpen(!columnSelectorOpen);
  };

  const toolbarDivider = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
  // Mobile: cresce até a largura livre da linha; desktop: nunca cresce, só encolhe.
  const flexibleLeftFlex = { xs: '1 1 auto', sm: '0 1 auto' } as const;
  const iconOnlyButtonSx = {
    minWidth: 36,
    width: 36,
    height: 36,
    minHeight: 36,
    maxHeight: 36,
    p: 0,
    lineHeight: 1,
    aspectRatio: '1 / 1',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiButton-startIcon': { display: 'none', m: 0 },
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      width: 20,
      height: 20,
    },
    ...(touchFriendly
      ? { minWidth: 44, width: 44, height: 44, minHeight: 44, maxHeight: 44 }
      : {}),
  } as const;

  useLayoutEffect(() => {
    if (!compactActionButtons) {
      setOverflowCompact(false);
      return undefined;
    }
    if (isSmallScreen) return undefined;

    const row = toolbarRowRef.current;
    const left = toolbarLeftRef.current;
    const right = toolbarRightRef.current;
    if (!row || !left || !right) return undefined;

    const LABEL_EXTRA_PX = 320;
    const update = () => {
      const style = window.getComputedStyle(row);
      const pad =
        (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const gap = parseFloat(style.columnGap || style.gap) || 12;
      const available = row.clientWidth - pad;
      const needed = left.scrollWidth + right.scrollWidth + gap;

      setOverflowCompact((current) => {
        if (!current && needed > available + 2) return true;
        if (current && needed + LABEL_EXTRA_PX < available) return false;
        return current;
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(row);
    observer.observe(left);
    observer.observe(right);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [compactActionButtons, isSmallScreen, rightExtraContent, leftExtraContent, showExcelButton, columns.length]);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          width: '100%',
          minWidth: 0,
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          border: 'none',
          borderRadius: 0,
          borderBottom: `1px solid ${toolbarDivider}`,
        }}
      >
        <Box
          ref={toolbarRowRef}
          sx={{
            p: 1.5,
            display: 'flex',
            gap: { xs: 1, sm: 1.5 },
            alignItems: 'center',
            justifyContent: 'space-between',
            minWidth: 0,
            width: '100%',
            flexWrap: compactActionButtons ? 'nowrap' : 'wrap',
          }}
        >
          <Box
            ref={toolbarLeftRef}
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 1.5 },
              alignItems: 'center',
              flex: leftExtraContentFlexible ? flexibleLeftFlex : '0 0 auto',
              minWidth: 0,
              flexWrap: 'nowrap',
            }}
          >
            {leftExtraContent && (
              <Box
                sx={
                  leftExtraContentFlexible
                    ? { flex: flexibleLeftFlex, minWidth: compactActionButtons ? { xs: 180, sm: 240 } : 0 }
                    : { flexShrink: 0 }
                }
              >
                {leftExtraContent}
              </Box>
            )}
            {columns.length > 0 && (
            <Tooltip title="Adicionar Filtro">
            <Button
              variant="outlined"
              startIcon={isCompactActions ? undefined : <FilterIcon />}
              onClick={onOpenFilterDialog}
              size="small"
              aria-label="Adicionar Filtro"
              sx={{
                flexShrink: 0,
                whiteSpace: 'nowrap',
                ...(isCompactActions ? iconOnlyButtonSx : { minWidth: isSmallScreen ? '0' : 'auto' }),
                ...(touchFriendly && !isCompactActions ? { minHeight: 44 } : {}),
                ...(isDark
                  ? {
                      borderColor: '#FFFFFF',
                      color: '#FFFFFF',
                      '&:hover': {
                        borderColor: '#E5E7EB',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                      },
                    }
                  : {}),
                '& .MuiSvgIcon-root': {
                  ...(isCompactActions ? { fontSize: 20, width: 20, height: 20 } : {}),
                  ...(isDark ? { color: '#FFFFFF' } : {}),
                },
              }}
            >
              {isCompactActions ? <FilterIcon /> : 'Adicionar Filtro'}
            </Button>
            </Tooltip>
            )}
            
            {/* Seletor de Colunas Visíveis (escondido no mobile) */}
            {!isSmallScreen && tableColumns && onColumnVisibilityChange && (
              <Box sx={{ flexShrink: 0 }}>
                <ColumnSelector
                  columns={tableColumns}
                  onColumnVisibilityChange={onColumnVisibilityChange}
                  onColumnReorder={onColumnReorder}
                  onResetToDefault={onResetToDefaultColumns}
                  onShowAll={onShowAllColumns}
                  enableDragAndDrop={enableColumnDragAndDrop}
                  isExpanded={columnSelectorOpen}
                  onToggleExpansion={toggleColumnSelector}
                  iconOnly={isCompactActions}
                />
              </Box>
            )}
            
            {/* Botão Excel */}
            {showExcelButton && onExportExcel && (
              <Tooltip title="Excel">
              <Button
                variant="outlined"
                startIcon={isCompactActions ? undefined : <FileDownloadIcon />}
                onClick={onExportExcel}
                size="small"
                aria-label="Excel"
                sx={{
                  borderColor: '#4caf50',
                  color: '#4caf50',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  ...(isCompactActions ? iconOnlyButtonSx : {}),
                  '&:hover': {
                    borderColor: '#2e7d32',
                    backgroundColor: 'rgba(76, 175, 80, 0.04)'
                  },
                  '& .MuiSvgIcon-root': isCompactActions
                    ? { fontSize: 20, width: 20, height: 20 }
                    : undefined,
                }}
              >
                {isCompactActions ? <FileDownloadIcon /> : 'Excel'}
              </Button>
              </Tooltip>
            )}
            
            {showRefreshButton && onRefresh && (
              <IconButton
                onClick={onRefresh}
                size="small"
                aria-label="Atualizar"
                sx={{ flexShrink: 0, ...(touchFriendly ? { width: 44, height: 44 } : {}) }}
              >
                <RefreshIcon />
              </IconButton>
            )}
          </Box>

          <Box
            ref={toolbarRightRef}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'flex-end',
              flex: '0 0 auto',
              ml: 'auto',
              flexWrap: compactActionButtons ? 'nowrap' : 'wrap',
            }}
          >
            {rightExtraContent}
            {onCreateNew && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateNew}
                size="small"
                sx={{
                  flex: { xs: '1 1 auto', sm: '0 0 auto' },
                  minWidth: { xs: 0, sm: 'auto' },
                  ...(touchFriendly ? { minHeight: 44 } : {}),
                  whiteSpace: 'nowrap',
                  backgroundColor: createButtonBg,
                  border: '1px solid',
                  borderColor: createButtonBorder,
                  color: '#FFFFFF',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: createButtonHoverBg,
                    borderColor: createButtonBorder,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  },
                }}
              >
                {createButtonText}
              </Button>
            )}
          </Box>
        </Box>
        
        {/* Filtros Ativos */}
        {activeFilters.length > 0 && (
          <Box
            sx={{
              p: 1.5,
              pt: 1,
              borderTop: `1px solid ${toolbarDivider}`,
              backgroundColor: 'transparent',
            }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {activeFilters.map((filter) => (
                <Chip
                  key={filter.id}
                  label={filter.label}
                  variant="filled"
                  size="small"
                  color="primary"
                  onDelete={() => onDeleteFilter(filter.id)}
                  onClick={() => onEditFilter(filter)}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 800,
                    ...(touchFriendly ? { height: 44, maxWidth: '100%' } : {}),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, isDark ? 0.50 : 0.35),
                    backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12),
                    borderRadius: 1,
                    color: isDark ? alpha(theme.palette.common.white, 0.92) : theme.palette.primary.dark,
                    '& .MuiChip-label': {
                      px: 1,
                    },
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.error.main,
                      '&:hover': {
                        color: theme.palette.error.dark,
                      },
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.28 : 0.16),
                    },
                    '&:active': {
                      transform: 'translateY(0.5px)',
                    },
                    transition: 'background-color 0.15s ease, transform 0.05s ease',
                  }}
                  deleteIcon={
                    touchFriendly ? (
                      <CancelIcon sx={{ fontSize: 24, boxSizing: 'content-box', p: '10px', m: '0 !important' }} />
                    ) : (
                      <CloseIcon />
                    )
                  }
                />
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Seletor de Colunas Visíveis expandido ABAIXO do cartão de filtros */}
      {tableColumns && onColumnVisibilityChange && columnSelectorOpen && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
            border: 'none',
            borderRadius: 0,
            borderBottom: `1px solid ${toolbarDivider}`,
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {enableColumnDragAndDrop 
                  ? '🖱️ Arraste o ícone ⋮⋮ para reordenar • ☑️ Clique para mostrar/ocultar'
                  : '☑️ Clique para mostrar/ocultar colunas'
                }
              </Typography>
              <Box display="flex" gap={1}>
                {onResetToDefaultColumns && (
                  <Button
                    size="small"
                    onClick={onResetToDefaultColumns}
                    variant="text"
                    sx={{ fontSize: '0.75rem', p: 0.5, minWidth: 'auto' }}
                  >
                    Padrão
                  </Button>
                )}
                {onShowAllColumns && (
                  <Button
                    size="small"
                    onClick={onShowAllColumns}
                    variant="text"
                    sx={{ fontSize: '0.75rem', p: 0.5, minWidth: 'auto' }}
                  >
                    Todas
                  </Button>
                  )}
              </Box>
            </Box>
            
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <Box display="flex" flexWrap="wrap" gap={1.5}>
                {tableColumns.map((column) => (
                  <DraggableColumnItem
                    key={column.id}
                    column={column}
                    onVisibilityChange={onColumnVisibilityChange}
                    enableDragAndDrop={enableColumnDragAndDrop}
                  />
                ))}
              </Box>
              
              <DragOverlay>
                {draggedColumn ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 0.5,
                      borderRadius: 1,
                      backgroundColor: theme.palette.background.paper,
                      border: `2px dashed ${theme.palette.primary.main}`,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 8px 25px rgba(0,0,0,0.4)'
                        : '0 8px 25px rgba(0,0,0,0.2)',
                      opacity: 0.8,
                    }}
                  >
                    {enableColumnDragAndDrop && (
                      <DragIcon 
                        sx={{ 
                          fontSize: 18, 
                          color: theme.palette.primary.main,
                        }} 
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={draggedColumn.visible}
                          disabled={draggedColumn.required}
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" color="text.primary">
                            {draggedColumn.label}
                          </Typography>
                          {draggedColumn.required && (
                            <Typography variant="caption" color="text.secondary">
                              *
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ 
                        minWidth: 'fit-content',
                        '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                        m: 0
                      }}
                    />
                  </Box>
                ) : null}
              </DragOverlay>
            </DndContext>
            
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                * Colunas obrigatórias que não podem ser escondidas
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <FilterDialog
        open={filterDialogOpen}
        onClose={onCloseFilterDialog}
        selectedColumn={selectedColumn}
        filterValue={filterValue}
        onColumnChange={onColumnChange}
        onValueChange={onValueChange}
        onAddFilter={onAddFilter}
        columns={columns}
        editingFilterId={editingFilterId}
        fullScreen={fullScreenFilterDialog}
      />
    </>
  );
};
